import * as GlobalS from "@/components/styled";
import { FieldNames, FormType, KeyPerformanceIndicators } from "@/enums";
import { IProjectTemplate } from "@/interfaces";
import {
  useFormPropertyValueLookupProvider,
  useProjectTemplateContext,
} from "@/providers";
import { Checkbox, Form, FormInstance, Input } from "antd";
import { Rule } from "antd/es/form";
import { useIntl } from "react-intl";
import * as S from "./styled";

const REQUIRED_KPI_COUNT = 4;

interface IProps {
  form: FormInstance;
  projectTemplate: IProjectTemplate;
}

export const TemplateDetailsEditor = ({ form, projectTemplate }: IProps) => {
  const intl = useIntl();
  const { saveProjectTemplate } = useProjectTemplateContext();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  const defaultKpis = [
    KeyPerformanceIndicators.Performance,
    KeyPerformanceIndicators.Cost,
    KeyPerformanceIndicators.Risk,
    KeyPerformanceIndicators.Schedule,
  ];

  const vigorValidator: Rule[] = [
    {
      required: true,
      message: intl.formatMessage({
        id: "editProjectTemplate.urlRequiredError",
      }),
    },
    {
      validator: (
        _: unknown,
        value: string | string[]
      ): Promise<void | Error> => {
        if (!value || value.includes("http://") || value.includes("https://"))
          return Promise.resolve();

        return Promise.reject(
          new Error(
            intl.formatMessage({
              id: "editProjectTemplate.urlHttpErrorMessage",
            })
          )
        );
      },
    },
  ];

  const kpiValidator: Rule[] = [
    {
      validator: (_: unknown, value: unknown[]): Promise<void | Error> => {
        if (value.length === REQUIRED_KPI_COUNT) return Promise.resolve();

        return Promise.reject(
          new Error(
            intl.formatMessage(
              {
                id: `editProjectTemplate.kpiErrorMessage${
                  value.length > REQUIRED_KPI_COUNT ? "High" : "Low"
                }`,
              },
              { count: REQUIRED_KPI_COUNT }
            )
          )
        );
      },
    },
  ];

  const handleSubmit = ({
    vigorTemplateUrl,
    projectKpis,
    isCtqDisabled,
  }: IProjectTemplate): void => {
    saveProjectTemplate({
      ...projectTemplate,
      vigorTemplateUrl,
      projectKpis,
      isCtqDisabled,
    });
  };

  const formInitialValues = {
    ...projectTemplate,
    Kpis: projectTemplate?.projectKpis?.length
      ? projectTemplate.projectKpis
      : defaultKpis,
  };

  return (
    <GlobalS.PageContentCard
      title={getPropertyValueDisplay(
        FormType.Intake,
        FieldNames.ProjectType,
        projectTemplate?.projectTypeId
      )}
    >
      <Form
        form={form}
        layout="vertical"
        name="document-form"
        initialValues={formInitialValues}
        onFinish={handleSubmit}
      >
        <S.FormItemBold
          name={"vigorTemplateUrl"}
          label={intl.formatMessage({
            id: "editProjectTemplate.vigorTemplatesTitle",
          })}
          normalize={(value): string => value.replace(/\s+/g, "")}
          rules={vigorValidator}
        >
          <Input />
        </S.FormItemBold>
        <S.FormItemBold
          name={"Kpis"}
          label={intl.formatMessage({
            id: "editProjectTemplate.kpiIndicators",
          })}
          rules={kpiValidator}
        >
          <Checkbox.Group
            data-test-id={`kpi-checkbox-group`}
            options={Object.keys(KeyPerformanceIndicators).map((kpi) => ({
              label: intl.formatMessage({
                id: `kpiLabels.${kpi.toLocaleLowerCase()}`,
              }),
              value: kpi,
            }))}
          />
        </S.FormItemBold>
        <S.FormItemBold name={"isCtqDisabled"} valuePropName="checked">
          <Checkbox data-test-id="disable-ctq-link-checkbox">
            {intl.formatMessage({
              id: "editProjectTemplate.isCtqDisabledLabel",
            })}
          </Checkbox>
        </S.FormItemBold>
      </Form>
    </GlobalS.PageContentCard>
  );
};
